import type {
  NotificationDetailResponse,
  NotificationRunStatus,
  ServiceLaneSearchResponse,
  ShipmentVendorMatchResponse,
} from '../../api/types';

export interface RankedSuggestion {
  lane: ServiceLaneSearchResponse;
  /** True when the fleet manager's lane matched the shipment's truck type. */
  truckTypeMatch: boolean;
  /** Match type: 'EXACT_LANE' when origin and destination match; 'ORIGIN_ONLY' when only origin matches */
  matchType?: 'EXACT_LANE' | 'ORIGIN_ONLY';
  /** Automated match score [0..100] when populated from matching engine */
  matchScore?: number;
  /** Algorithmic rank order assigned by the matching engine (1-based) */
  rankOrder?: number;
  /** Parsed multi-factor score breakdown map */
  scoreBreakdown?: Record<string, number>;
  /** Whether candidate was generated by the automated matching engine */
  isAutomatedMatch?: boolean;
  /** Delivery status of vendor in notification runs if available */
  notificationStatus?: string;
  /** Wave number when vendor was alerted */
  notificationWave?: number;
}

/**
 * Safely parses the JSON string representation of factor weights and scores.
 */
export function parseScoreBreakdown(
  rawJson: string | null | undefined,
): Record<string, number> {
  if (!rawJson) return {};
  try {
    const parsed = JSON.parse(rawJson);
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, number>;
    }
  } catch {
    // Ignore parse error and fallback to empty map
  }
  return {};
}

/**
 * Humanizes factor names for score breakdown tooltips (e.g. "lane_match" -> "Lane Match").
 */
export function humanizeFactorName(factor: string): string {
  return factor
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Returns color tokens for score badge presentation based on match quality.
 */
export function getScoreTheme(score: number): {
  bg: string;
  text: string;
  border: string;
  badgeLabel: string;
} {
  if (score >= 80) {
    return {
      bg: 'var(--status-green-bg)',
      text: 'var(--status-green-text)',
      border: 'var(--status-green-border)',
      badgeLabel: 'High Match',
    };
  }
  if (score >= 50) {
    return {
      bg: 'var(--status-amber-bg)',
      text: 'var(--status-amber-text)',
      border: 'var(--status-amber-border)',
      badgeLabel: 'Good Match',
    };
  }
  return {
    bg: 'var(--status-gray-bg)',
    text: 'var(--muted)',
    border: 'var(--line-strong)',
    badgeLabel: 'Partial Match',
  };
}

/**
 * Returns true if a notification run is currently active or pending next waves,
 * requiring telemetry polling. Terminal runs (COMPLETED, CANCELLED, FAILED) return false.
 */
export function isNotificationRunActive(
  status?: NotificationRunStatus | null,
): boolean {
  return status === 'IN_PROGRESS' || status === 'SCHEDULED';
}

/** KYC-verified first, then fleet size descending, then company name as a stable tiebreak. */
export function rankLanes(lanes: ServiceLaneSearchResponse[]): ServiceLaneSearchResponse[] {
  return [...lanes].sort((a, b) => {
    const aVerified = a.verificationStatus === 'VERIFIED' ? 0 : 1;
    const bVerified = b.verificationStatus === 'VERIFIED' ? 0 : 1;
    if (aVerified !== bVerified) return aVerified - bVerified;
    const sizeDiff = (b.fleetSize ?? 0) - (a.fleetSize ?? 0);
    if (sizeDiff !== 0) return sizeDiff;
    return (a.companyName ?? '').localeCompare(b.companyName ?? '');
  });
}

/**
 * Build the top suggestions across 4 prioritized tiers:
 * Tier 1: Exact Lane (From → To) + Required Truck Type Match
 * Tier 2: Exact Lane (From → To) (Any Truck Type)
 * Tier 3: Origin Only (From) + Required Truck Type Match
 * Tier 4: Origin Only (From) (Any Truck Type)
 * Deduplicated by fleet manager, capped at `limit`.
 */
export function buildSuggestions(
  exactTruckMatches?: ServiceLaneSearchResponse[],
  exactLaneMatches?: ServiceLaneSearchResponse[],
  originTruckMatches?: ServiceLaneSearchResponse[],
  originLaneMatches?: ServiceLaneSearchResponse[],
  limit = 5,
): RankedSuggestion[] {
  const tier1 = rankLanes(exactTruckMatches ?? []);
  const seenIds = new Set(tier1.map((l) => l.fleetManagerId));

  const tier2 = rankLanes(
    (exactLaneMatches ?? []).filter((l) => !seenIds.has(l.fleetManagerId)),
  );
  tier2.forEach((l) => seenIds.add(l.fleetManagerId));

  const tier3 = rankLanes(
    (originTruckMatches ?? []).filter((l) => !seenIds.has(l.fleetManagerId)),
  );
  tier3.forEach((l) => seenIds.add(l.fleetManagerId));

  const tier4 = rankLanes(
    (originLaneMatches ?? []).filter((l) => !seenIds.has(l.fleetManagerId)),
  );

  return [
    ...tier1.map((lane) => ({ lane, truckTypeMatch: true, matchType: 'EXACT_LANE' as const })),
    ...tier2.map((lane) => ({ lane, truckTypeMatch: false, matchType: 'EXACT_LANE' as const })),
    ...tier3.map((lane) => ({ lane, truckTypeMatch: true, matchType: 'ORIGIN_ONLY' as const })),
    ...tier4.map((lane) => ({ lane, truckTypeMatch: false, matchType: 'ORIGIN_ONLY' as const })),
  ].slice(0, limit);
}

/**
 * Merges automated matches from the scoring engine with fallback service lane data.
 * When automated matches exist, they appear first ordered by rankOrder.
 */
export function mergeAutomatedMatches(
  automatedMatches: ShipmentVendorMatchResponse[] | undefined,
  fallbackSuggestions: RankedSuggestion[],
  lanesByVendorId: Map<number, ServiceLaneSearchResponse> = new Map(),
  notificationsByVendorId?: Map<number, NotificationDetailResponse>,
): RankedSuggestion[] {
  if (!automatedMatches || automatedMatches.length === 0) {
    return fallbackSuggestions;
  }

  // Suppress fallback rows for every vendor evaluated by the matching engine.
  // Ineligible vendors must never become actionable again through lane fallback.
  const automatedVendors = new Set(automatedMatches.map((match) => match.vendorId));
  const results: RankedSuggestion[] = [];

  const sortedMatches = automatedMatches
    .filter((match) => match.eligibilityStatus === 'ELIGIBLE')
    .sort((a, b) => (a.rankOrder ?? 999) - (b.rankOrder ?? 999));

  for (const match of sortedMatches) {
    const existingLane = lanesByVendorId.get(match.vendorId);
    const notification = notificationsByVendorId?.get(match.vendorId);

    const laneData: ServiceLaneSearchResponse = existingLane ?? {
      laneId: match.id,
      fromState: '',
      toState: '',
      truckTypes: [],
      fleetManagerId: match.vendorId,
      companyName: match.vendorName,
      fleetSize: 0,
      // Eligibility is not KYC: no lane means no known KYC status, so none is shown.
      phoneNumber: match.vendorPhone,
      whatsappNumber: match.vendorPhone,
    };

    const breakdown = parseScoreBreakdown(match.scoreBreakdown);
    const laneMatchScore = breakdown['LANE_MATCH'] ?? breakdown['lane_match'];
    const isOriginOnly = laneMatchScore !== undefined && laneMatchScore < 80;
    const matchType: 'EXACT_LANE' | 'ORIGIN_ONLY' = isOriginOnly ? 'ORIGIN_ONLY' : 'EXACT_LANE';

    results.push({
      lane: laneData,
      truckTypeMatch: true,
      matchType,
      matchScore: Number(match.matchScore),
      rankOrder: match.rankOrder,
      scoreBreakdown: breakdown,
      isAutomatedMatch: true,
      notificationStatus: notification?.status,
      notificationWave: notification?.waveNumber,
    });
  }

  // Append any fallback suggestions that weren't in automated matches
  for (const fallback of fallbackSuggestions) {
    if (!automatedVendors.has(fallback.lane.fleetManagerId)) {
      results.push(fallback);
    }
  }

  return results;
}
